import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "../../store";
import {
  deleteCategory,
  getCategoryId,
  resetCategory,
  searchCategories,
  setCategoryFilter,
} from "../../slices/category";
import {
  deleteUser,
  getUserId,
  getUsers,
  resetUser,
  searchUsers,
  setUserFilter,
} from "../../slices/user";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { Category, Work, Attachment, Event, Person } from "@mui/icons-material";
import Categories from "../views/Settings/Categories";
import SearchParams, { defaultParams } from "../../types/searchParams";
import SettingsModal from "../../types/settingsModal";
import TabNavButton from "../../types/tabNavButton";
import { Base } from "../base";
import JobTypes from "../views/Settings/JobTypes";
import {
  deleteJobType,
  getJobTypeId,
  getJobTypes,
  resetJobType,
  searchJobTypes,
  setJobTypeFilter,
} from "../../slices/jobType";
import AttachmentTypes from "../views/Settings/AttachmentTypes";
import {
  deleteAttachmentType,
  getAttachmentType,
  resetAttachmentType,
  searchAttachmentTypes,
  setAttachmentTypeFilter,
} from "../../slices/attachmentType";
import {
  deleteHighlight,
  getHighlight,
  resetHighlight,
  searchHighlights,
  setHighlightFilter,
} from "../../slices/calendarHighlight";
import CalendarHighlight from "../views/Settings/CalendarHighlight";
import Users from "../views/Settings/Users";

interface Props {}

const SettingsPage: React.FC<Props> = (props) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { user, status: userStatus } = useSelector((store) => store.user);
  const { jobType, status: jobTypeStatus } = useSelector(
    (store) => store.jobType,
  );
  const { category, status: categoryStatus } = useSelector(
    (store) => store.category,
  );
  const { attachmentType, status: attachmentTypeStatus } = useSelector(
    (store) => store.attachmentType,
  );
  const { highlight, status: highlightStatus } = useSelector(
    (store) => store.highlight,
  );
  const xs = useMediaQuery(theme.breakpoints.down("sm"));
  const [activeTab, setActiveTab] = useState(0);
  const [deleteModal, setDeleteModal] = useState<SettingsModal>({
    visible: false,
  });

  useEffect(() => {
    if (!xs) {
      switch (activeTab) {
        case 0: //job types
          dispatch(searchJobTypes(defaultParams));
          dispatch(setJobTypeFilter(defaultParams));
          break;
        case 1: //users
          dispatch(getUsers());
          dispatch(getJobTypes());
          break;
        case 2: //categories
          dispatch(searchCategories(defaultParams));
          dispatch(setCategoryFilter(defaultParams));
          break;
        case 3: // attachmentTypes
          dispatch(searchAttachmentTypes(defaultParams));
          dispatch(setAttachmentTypeFilter(defaultParams));
          break;
        case 4: // calendar Highlights
          dispatch(searchHighlights(defaultParams));
          dispatch(setHighlightFilter(defaultParams));
          break;
        default:
          break;
      }
    }
  }, [dispatch, activeTab, xs]);

  const handleSearch = async (
    search: SearchParams,
    type: "user" | "category" | "jobType" | "attachmentType" | "highlight",
  ) => {
    switch (type) {
      case "user":
        const userResult: any = await dispatch(searchUsers(search));
        if (userResult.payload.results) {
          return userResult.payload.results;
        } else return [];
      case "category":
        const categoryResult: any = await dispatch(searchCategories(search));
        if (categoryResult.payload.results) {
          return categoryResult.payload.results;
        } else return [];
      case "jobType":
        const jobTypeResult: any = await dispatch(searchJobTypes(search));
        if (jobTypeResult.payload.results) {
          return jobTypeResult.payload.results;
        } else return [];
      case "attachmentType":
        const attachmentTypeResult: any = await dispatch(
          searchAttachmentTypes(search),
        );
        if (attachmentTypeResult.payload.results) {
          return attachmentTypeResult.payload.results;
        } else return [];
      case "highlight":
        const highlightResult: any = await dispatch(searchHighlights(search));
        if (highlightResult.payload.results) {
          return highlightResult.payload.results;
        } else return [];
      default:
        break;
    }
  };

  const handleDelete = async () => {
    switch (deleteModal.modal) {
      case "user":
        dispatch(deleteUser(user.id)).then(() => {
          dispatch(searchUsers(defaultParams));
          dispatch(resetUser());
        });
        break;
      case "jobType":
        dispatch(deleteJobType(jobType.id)).then(() => {
          dispatch(searchJobTypes(defaultParams));
          dispatch(resetJobType());
        });
        break;
      case "categories":
        dispatch(deleteCategory(category.id)).then(() => {
          dispatch(searchCategories(defaultParams));
          dispatch(resetCategory());
        });
        break;
      // case "communicationMethod":
      //   dispatch(deleteCommunicationMethod(communicationMethod.id)).then(() => {
      //     dispatch(searchCommunicationMethod(defaultParams));
      //     dispatch(resetCommunicationMethod());
      //   });
      //   break;
      // case "jobSource":
      //   dispatch(deleteJobSource(jobSource.id)).then(() => {
      //     dispatch(searchJobSource(defaultParams));
      //     dispatch(resetJobSource());
      //   });
      //   break;
      case "attachmentType":
        dispatch(deleteAttachmentType(attachmentType.id)).then(() => {
          dispatch(searchAttachmentTypes(defaultParams));
          dispatch(resetAttachmentType());
        });
        break;
      case "highlight":
        dispatch(deleteHighlight(highlight.id)).then(() => {
          dispatch(searchHighlights(defaultParams));
          dispatch(resetHighlight());
        });
        break;

      default:
        break;
    }
    await setDeleteModal({ visible: false, modal: undefined });
  };

  const tabs: TabNavButton[] = [
    {
      id: 0,
      label: "Job Types",
      element: (
        <JobTypes
          apiJobTypeSearch={(search) => handleSearch(search, "jobType")}
          apiGetJobType={(id) => dispatch(getJobTypeId(id))}
          resetJobType={() => {
            dispatch(resetJobType());
          }}
          phone={xs}
          setDeleteModal={setDeleteModal}
          setJobTypeFilter={(params: SearchParams) => {
            dispatch(setJobTypeFilter(params));
          }}
          deleteModal={deleteModal}
        />
      ),
      icon: <Work />,
    },
    {
      id: 1,
      label: "Users",
      element: (
        <Users
          // apiUserSearch={(search) => handleSearch(search, "user")}
          getUsers={() => dispatch(getUsers())}
          apiGetUser={(id: number) => {
            dispatch(getUserId(id));
          }}
          resetUser={() => {
            dispatch(resetUser());
          }}
          phone={xs}
          setDeleteModal={setDeleteModal}
          setUserFilter={(params: SearchParams) => {
            dispatch(setUserFilter(params));
          }}
        />
      ),
      icon: <Person />,
    },
    {
      id: 2,
      label: "Categories",
      element: (
        <Categories
          apiCategorySearch={(search) => handleSearch(search, "category")}
          apiGetCategory={(id: number) => {
            dispatch(getCategoryId(id));
          }}
          resetCategory={() => {
            dispatch(resetCategory());
          }}
          phone={xs}
          setDeleteModal={setDeleteModal}
          setCategoryFilter={(params: SearchParams) => {
            dispatch(setCategoryFilter(params));
          }}
          deleteModal={deleteModal}
        />
      ),
      icon: <Category />,
    },
    {
      id: 3,
      label: "Attachment Types",
      element: (
        <AttachmentTypes
          apiAttachmentTypeSearch={(search) =>
            handleSearch(search, "attachmentType")
          }
          apiGetAttachmentType={(id) => dispatch(getAttachmentType(id))}
          resetAttachmentType={() => dispatch(resetAttachmentType())}
          phone={xs}
          setDeleteModal={setDeleteModal}
          setAttachmentTypeFilter={(params) =>
            dispatch(setAttachmentTypeFilter(params))
          }
          deleteModal={deleteModal}
        />
      ),
      icon: <Attachment />,
    },
    {
      id: 4,
      label: "Calendar Highlights",
      element: (
        <CalendarHighlight
          apiHighlightSearch={(search) => handleSearch(search, "highlight")}
          apiGetHighlight={(id) => dispatch(getHighlight(id))}
          resetHighlight={() => dispatch(resetHighlight())}
          phone={xs}
          setDeleteModal={setDeleteModal}
          setHighlightFilter={(params) => dispatch(setHighlightFilter(params))}
          deleteModal={deleteModal}
        />
      ),
      icon: <Event />,
    },
  ];

  const deleteUserMessage = `Are you sure you would like to delete ${user.firstName} ${user.lastName}?`;
  const deleteJobTypeMessage = `Are you sure you would like to delete ${jobType.name}? This will delete all categories and jobs associated with it.`;
  const deleteCategoryMessage = `Are you sure you would like to delete ${category.name}?`;
  const deleteAttachmentTypeMessage = `Are you sure you would like to delete ${attachmentType.name}?`;
  const deleteHighlightMessage = `Are you sure you would like to delete ${highlight.name}?`;

  return (
    <Box
      sx={{
        margin: { md: 5, xs: 0 },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Base.TitleBar title={tabs[activeTab].label} />
      <Base.TabNav
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        tabs={tabs}
      />
      <Base.Dialog
        open={deleteModal.visible}
        title={
          deleteModal.modal === "user"
            ? deleteUserMessage
            : deleteModal.modal === "categories"
            ? deleteCategoryMessage
            : deleteModal.modal === "jobType"
            ? deleteJobTypeMessage
            : deleteModal.modal === "attachmentType"
            ? deleteAttachmentTypeMessage
            : deleteModal.modal === "highlight"
            ? deleteHighlightMessage
            : ""
        }
        handleCancel={() => {
          setDeleteModal({ visible: false, modal: undefined });
        }}
        confirmText="Delete"
        confirmButtonType="delete"
        handleConfirm={handleDelete}
        loading={
          deleteModal.modal === "user"
            ? userStatus === "loading"
            : deleteModal.modal === "categories"
            ? categoryStatus === "loading"
            : deleteModal.modal === "jobType"
            ? jobTypeStatus === "loading"
            : deleteModal.modal === "attachmentType"
            ? attachmentTypeStatus === "loading"
            : deleteModal.modal === "highlight"
            ? highlightStatus === "loading"
            : false
        }
      />
    </Box>
  );
};

export default SettingsPage;
